import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Card,
  CardContent,
  Typography,
  TextField,
  Stack,
  Divider,
  Box,
  Button,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tab,
  Tabs,
  GlobalStyles,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import PrintIcon from '@mui/icons-material/Print';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import LabelObjectRenderer from '@/components/labelDesigner/LabelObjectRenderer';
import { CANVAS_SCALE } from '@/components/labelDesigner/LabelCanvas';
import { apiClient } from '@/api/axiosClient';
import { applySequenceValue } from '@/lib/autoGenerate';
import { peekSequenceValues, reserveSequenceValues } from '@/lib/sequenceApi';
import { useDateFormat } from '@/hooks/useDateFormat';
import type { LabelObject, LabelTemplate } from '@/types/labelTemplate';
import type { MaterialDto } from '@/types/materials';
import type { ProductionReceiptDto, ProductionReceiptLineDto } from '@/types/production';
import type { ProductionPrintedLabelDto, ProductionPrintHistoryDto, SubmitProductionPrintJobRequest } from '@/types/productionLabeling';

// Smaller than the label designer's own CANVAS_SCALE — this preview shows one tile per physical
// label (could be dozens under Auto mode), so a slightly more compact scale keeps the grid
// scannable instead of each tile matching the designer's full working size.
const PREVIEW_SCALE = CANVAS_SCALE * 0.7;

interface PrintJobGroup {
  id: string;
  templateId: string;
  templateName: string;
  printedDate: string;
  printer: string;
  labels: ProductionPrintedLabelDto[];
  totalQty: number;
}

export default function ProductionLabelPrintPage() {
  const { t } = useTranslation('productionLabeling');
  const { formatDate, formatDateTime } = useDateFormat();
  const queryClient = useQueryClient();

  const { data: receipts = [] } = useQuery({
    queryKey: ['production-receipts'],
    queryFn: async () => (await apiClient.get<ProductionReceiptDto[]>('/production-receipts')).data,
  });
  const { data: templates = [] } = useQuery({
    queryKey: ['label-templates'],
    queryFn: async () => (await apiClient.get<LabelTemplate[]>('/label-templates')).data,
  });
  const { data: history = [] } = useQuery({
    queryKey: ['production-print-history'],
    queryFn: async () => (await apiClient.get<ProductionPrintHistoryDto[]>('/production-print-history')).data,
  });

  const [pageTab, setPageTab] = useState<'print' | 'reprint'>('print');

  const [receiptId, setReceiptId] = useState('');
  const [receiptLineId, setReceiptLineId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [printMode, setPrintMode] = useState<'auto' | 'manual'>('auto');
  const [manualRows, setManualRows] = useState<number[]>([]);
  const [printBatch, setPrintBatch] = useState<LabelObject[][] | null>(null);
  const [printBatchQuantities, setPrintBatchQuantities] = useState<number[] | null>(null);
  const [printBatchTemplate, setPrintBatchTemplate] = useState<LabelTemplate | null>(null);
  const [printBatchSampleData, setPrintBatchSampleData] = useState<Record<string, string> | null>(null);

  const [reprintReceiptId, setReprintReceiptId] = useState('');
  const [reprintReceiptLineId, setReprintReceiptLineId] = useState('');

  // No BeforeQc/AfterQc branch — Production Receipt has no QC step, so the printable ceiling is
  // always AcceptedQty. Only lines on an Approved receipt are selectable at all — that gate is
  // applied at the receipt level below rather than per-line.
  const maxQtyFor = (line: ProductionReceiptLineDto) => line.acceptedQty;
  const remainingQtyFor = (line: ProductionReceiptLineDto) => Math.max(0, maxQtyFor(line) - line.labelPrintedQty);

  const isLineSelectable = useCallback((line: ProductionReceiptLineDto) => remainingQtyFor(line) > 0, []);

  const selectableReceipts = useMemo(
    () => receipts.filter((r) => r.status === 'Approved' && r.lines.some(isLineSelectable)),
    [receipts, isLineSelectable],
  );

  const selectedReceipt = selectableReceipts.find((r) => r.id === receiptId);
  const receiptLines = useMemo(() => (selectedReceipt?.lines ?? []).filter(isLineSelectable), [selectedReceipt, isLineSelectable]);

  const selectedLine = receiptLines.find((l) => l.id === receiptLineId);

  const { data: selectedMaterial } = useQuery({
    queryKey: ['materials', selectedLine?.materialId],
    queryFn: async () => (await apiClient.get<MaterialDto>(`/materials/${selectedLine!.materialId}`)).data,
    enabled: Boolean(selectedLine?.materialId),
  });

  // All ProductionPrintedLabel rows ever recorded (capped server-side) — used to work out which
  // receipts/lines actually have print history, so the Reprint tab only ever offers something
  // reprintable.
  const { data: allPrintedLabels = [] } = useQuery({
    queryKey: ['production-printed-labels'],
    queryFn: async () => (await apiClient.get<ProductionPrintedLabelDto[]>('/production-printed-labels')).data,
  });
  const printedReceiptLineIds = useMemo(() => new Set(allPrintedLabels.map((l) => l.productionReceiptLineId)), [allPrintedLabels]);

  const reprintableReceipts = useMemo(() => receipts.filter((r) => r.lines.some((l) => printedReceiptLineIds.has(l.id))), [receipts, printedReceiptLineIds]);
  const reprintReceipt = reprintableReceipts.find((r) => r.id === reprintReceiptId);
  const reprintReceiptLines = useMemo(() => (reprintReceipt?.lines ?? []).filter((l) => printedReceiptLineIds.has(l.id)), [reprintReceipt, printedReceiptLineIds]);
  const reprintLine = reprintReceiptLines.find((l) => l.id === reprintReceiptLineId);

  const { data: reprintMaterial } = useQuery({
    queryKey: ['materials', reprintLine?.materialId],
    queryFn: async () => (await apiClient.get<MaterialDto>(`/materials/${reprintLine!.materialId}`)).data,
    enabled: Boolean(reprintLine?.materialId),
  });

  const printedLabels = useMemo(() => allPrintedLabels.filter((l) => l.productionReceiptLineId === reprintReceiptLineId), [allPrintedLabels, reprintReceiptLineId]);

  const maxQty = selectedLine ? maxQtyFor(selectedLine) : 0;
  const remainingQty = selectedLine ? remainingQtyFor(selectedLine) : 0;

  // Splits remainingQty into Material.stuffingQty-sized chunks — e.g. 56 units at a stuffing qty
  // of 10 becomes [10,10,10,10,10,6] (5 full labels, the last covering the remainder). No Stuffing
  // Qty set (null/0) or a Stuffing Qty of 1 both mean "one unit per label".
  const autoBreakdown = useMemo(() => {
    if (remainingQty <= 0) return [];
    const rawStuffingQty = selectedMaterial?.stuffingQty;
    const stuffingQty = rawStuffingQty && rawStuffingQty > 0 ? rawStuffingQty : 1;

    const fullLabels = Math.floor(remainingQty / stuffingQty);
    const remainder = Math.round((remainingQty - fullLabels * stuffingQty) * 1000) / 1000;
    const breakdown = Array.from({ length: fullLabels }, () => stuffingQty);
    if (remainder > 0) breakdown.push(remainder);
    return breakdown;
  }, [remainingQty, selectedMaterial]);

  const labelQuantities = printMode === 'auto' ? autoBreakdown : manualRows;
  const manualTotal = manualRows.reduce((sum, q) => sum + q, 0);
  const manualValid = manualRows.length > 0 && manualRows.every((q) => q > 0) && manualTotal === remainingQty;

  useEffect(() => {
    if (receiptId && !selectableReceipts.some((r) => r.id === receiptId)) {
      setReceiptId('');
    }
  }, [selectableReceipts, receiptId]);

  useEffect(() => {
    if (receiptLineId && !receiptLines.some((l) => l.id === receiptLineId)) {
      setReceiptLineId('');
    }
  }, [receiptLines, receiptLineId]);

  useEffect(() => {
    setReceiptLineId('');
    setTemplateId('');
  }, [receiptId]);

  useEffect(() => {
    setReprintReceiptLineId('');
  }, [reprintReceiptId]);

  useEffect(() => {
    if (!selectedLine) return;
    const remaining = remainingQtyFor(selectedLine);
    setManualRows(remaining > 0 ? [remaining] : []);
    const activeTemplates = templates.filter((tpl) => tpl.status === 'Active' && tpl.assignmentType !== 'Packing' && tpl.assignmentType !== 'WeightProduction');
    const preferred = activeTemplates.find((tpl) => tpl.materialId === selectedLine.materialId) ?? activeTemplates.find((tpl) => !tpl.materialId);
    setTemplateId(preferred?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptLineId]);

  // Packing-assigned templates are excluded here — they're only offered on the Packing Label
  // Print screen, even if Active, so a layout built for package labels never leaks into Production.
  const availableTemplates = useMemo(
    () =>
      templates
        .filter((tpl) => tpl.status === 'Active' && tpl.assignmentType !== 'Packing' && tpl.assignmentType !== 'WeightProduction')
        .filter((tpl) => (tpl.materialId ? tpl.materialId === selectedLine?.materialId : true)),
    [templates, selectedLine],
  );
  const selectedTemplate = templates.find((tpl) => tpl.id === templateId);

  // Shared between Tab 1's live preview and Tab 2's reprint (which prints a different receipt/line
  // than whatever Tab 1 currently has selected) — everything except Qty, which varies per physical
  // label and gets merged in separately (see the #label-print-area render loop below).
  const buildBaseSampleData = useCallback(
    (line: ProductionReceiptLineDto | undefined, receipt: ProductionReceiptDto | undefined, material: MaterialDto | undefined) => ({
      MaterialCode: line?.materialCode || 'SKU-00000',
      MaterialName: line?.materialName || t('sampleMaterial'),
      CategoryName: material?.categoryName ?? '',
      UomCode: material?.uomCode ?? '',
      Barcode: material?.barcode ?? '',
      HsnCode: material?.hsnCode ?? '',
      GstRate: material?.gstRate != null ? String(material.gstRate) : '',
      ManufacturerName: material?.manufacturerName ?? '',
      AbcClass: material?.abcClass ?? '',
      ReceiptNumber: receipt?.receiptNumber ?? '',
      ProductionOrderNumber: receipt?.productionOrderNumber ?? receipt?.materialRequestNumber ?? '',
      WarehouseName: receipt?.warehouseName ?? '',
      ReceiptDate: receipt?.receiptDate ? formatDate(receipt.receiptDate) : '',
      Status: receipt?.status ?? '',
      BatchNo: line?.batchNo ?? '',
      MfgDate: line?.mfgDate ? formatDate(line.mfgDate) : '',
      ExpiryDate: line?.expiryDate ? formatDate(line.expiryDate) : '',
      ProducedQty: line ? String(line.producedQty) : '',
      AcceptedQty: line ? String(line.acceptedQty) : '',
      RejectedQty: line ? String(line.rejectedQty) : '',
      UnitCost: line?.unitCost != null ? String(line.unitCost) : '',
      PrintDate: dayjs().format('MMM D, YYYY'),
      PrintTime: dayjs().format('HH:mm'),
    }),
    [t, formatDate],
  );

  const sampleData = useMemo(
    () => ({
      ...buildBaseSampleData(selectedLine, selectedReceipt, selectedMaterial),
      Qty: labelQuantities[0] != null ? String(labelQuantities[0]) : '',
    }),
    [buildBaseSampleData, selectedLine, selectedReceipt, selectedMaterial, labelQuantities],
  );

  const totalLabelQty = labelQuantities.reduce((sum, q) => sum + q, 0);

  // Each auto-generating object gets its own run of values so every preview tile shows the barcode
  // that tile will actually carry. Peeked, never reserved — previewing must not consume barcode
  // numbers, so printing still reserves for real. See applySequenceValue for why the value has to be
  // stamped on with autoGenerate cleared, or the renderer keeps showing the configured start value.
  const autoGenObjects = useMemo(
    () => (selectedTemplate?.objects ?? []).filter(
      (o): o is Extract<LabelObject, { type: 'barcode' | 'qr' | 'text' }> =>
        (o.type === 'barcode' || o.type === 'qr' || o.type === 'text') && !!o.autoGenerate,
    ),
    [selectedTemplate],
  );

  const { data: previewValues } = useQuery({
    queryKey: ['label-sequence-peek', templateId, labelQuantities.length, autoGenObjects.map((o) => o.id).join(',')],
    queryFn: async () => {
      const map = new Map<string, string[]>();
      for (const obj of autoGenObjects) {
        map.set(obj.id, await peekSequenceValues(templateId, obj.id, obj.autoGenerate!, labelQuantities.length));
      }
      return map;
    },
    enabled: Boolean(templateId) && autoGenObjects.length > 0 && labelQuantities.length > 0,
  });
  const readyToPrint = Boolean(
    receiptLineId &&
      templateId &&
      selectedLine &&
      labelQuantities.length > 0 &&
      totalLabelQty > 0 &&
      totalLabelQty <= remainingQty &&
      (printMode === 'auto' || manualValid),
  );

  useEffect(() => {
    if (!printBatch) return;
    const handleAfterPrint = () => setPrintBatch(null);
    window.addEventListener('afterprint', handleAfterPrint);
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
      cancelAnimationFrame(raf);
    };
  }, [printBatch]);

  const printMutation = useMutation({
    mutationFn: async () => {
      if (!selectedLine || !selectedTemplate) throw new Error('Select an item and template first.');

      const autoGenObjects = selectedTemplate.objects.filter(
        (o): o is Extract<LabelObject, { type: 'barcode' | 'qr' | 'text' }> => (o.type === 'barcode' || o.type === 'qr' || o.type === 'text') && !!o.autoGenerate,
      );

      const labelCount = labelQuantities.length;
      let barcodeValues: string[] | undefined;

      if (autoGenObjects.length === 0) {
        setPrintBatch(Array.from({ length: labelCount }, () => selectedTemplate.objects));
      } else {
        const valuesByObjectId = new Map<string, string[]>();
        for (const obj of autoGenObjects) {
          valuesByObjectId.set(obj.id, await reserveSequenceValues(selectedTemplate.id, obj.id, obj.autoGenerate!, labelCount));
        }
        const instances = Array.from({ length: labelCount }, (_, i) =>
          selectedTemplate.objects.map((o) => {
            if (!valuesByObjectId.has(o.id)) return o;
            const value = valuesByObjectId.get(o.id)![i];
            return applySequenceValue(o, value);
          }),
        );
        setPrintBatch(instances);

        const barcodeObject = autoGenObjects.find((o) => o.type === 'barcode') ?? autoGenObjects.find((o) => o.type === 'qr') ?? autoGenObjects[0];
        barcodeValues = valuesByObjectId.get(barcodeObject.id);
      }

      setPrintBatchQuantities(labelQuantities);
      setPrintBatchTemplate(selectedTemplate);
      setPrintBatchSampleData(sampleData);

      const payload: SubmitProductionPrintJobRequest = {
        productionReceiptLineId: selectedLine.id, labelTemplateId: selectedTemplate.id, labelQuantities, printer: '', barcodeValues,
      };
      await apiClient.post('/production-print-jobs', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-print-history'] });
      queryClient.invalidateQueries({ queryKey: ['production-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['production-printed-labels'] });
    },
  });

  // Groups the line's accumulated ProductionPrintedLabel rows back into "print jobs" by
  // productionPrintHistoryEntryId — one row per job actually printed (not per physical label).
  const reprintJobGroups = useMemo<PrintJobGroup[]>(() => {
    const map = new Map<string, PrintJobGroup>();
    for (const label of printedLabels) {
      const existing = map.get(label.productionPrintHistoryEntryId);
      if (existing) {
        existing.labels.push(label);
        existing.totalQty += label.qty;
      } else {
        map.set(label.productionPrintHistoryEntryId, {
          id: label.productionPrintHistoryEntryId,
          templateId: label.labelTemplateId,
          templateName: label.templateName,
          printedDate: label.printedDate,
          printer: label.printer,
          labels: [label],
          totalQty: label.qty,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => (a.printedDate < b.printedDate ? 1 : -1));
  }, [printedLabels]);

  const reprintMutation = useMutation({
    mutationFn: async (group: PrintJobGroup) => {
      const template = templates.find((tpl) => tpl.id === group.templateId);
      if (!template) throw new Error('The original label template no longer exists.');

      // Re-uses each label's originally recorded BarcodeValue/Qty verbatim — never calls
      // reserveSequenceValues here, since a reprint must not advance or duplicate a barcode counter.
      const autoGenObjects = template.objects.filter(
        (o): o is Extract<LabelObject, { type: 'barcode' | 'qr' | 'text' }> => (o.type === 'barcode' || o.type === 'qr' || o.type === 'text') && !!o.autoGenerate,
      );
      const primary = autoGenObjects.find((o) => o.type === 'barcode') ?? autoGenObjects.find((o) => o.type === 'qr') ?? autoGenObjects[0];

      const instances = group.labels.map((label) =>
        template.objects.map((o) => {
          if (!primary || o.id !== primary.id || !label.barcodeValue) return o;
          return applySequenceValue(o, label.barcodeValue);
        }),
      );

      setPrintBatch(instances);
      setPrintBatchQuantities(group.labels.map((l) => l.qty));
      setPrintBatchTemplate(template);
      setPrintBatchSampleData(buildBaseSampleData(reprintLine, reprintReceipt, reprintMaterial));

      await apiClient.post(`/production-print-history/${group.id}/reprint`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-print-history'] });
    },
  });

  const historyColumns: DataTableColumn<ProductionPrintHistoryDto>[] = [
    { field: 'dateTime', headerName: t('history.date'), render: (r) => formatDateTime(r.dateTime) },
    { field: 'productionReceiptNumber', headerName: t('history.receipt'), render: (r) => r.productionReceiptNumber ?? '—' },
    { field: 'material', headerName: t('history.material'), render: (r) => `${r.materialCode} — ${r.material}` },
    { field: 'template', headerName: t('history.template'), filterable: true },
    { field: 'quantity', headerName: t('history.quantity') },
    { field: 'printer', headerName: t('history.printer'), filterable: true },
    { field: 'user', headerName: t('history.user'), filterable: true },
  ];

  const reprintColumns: DataTableColumn<PrintJobGroup>[] = [
    { field: 'printedDate', headerName: t('history.date'), render: (r) => formatDateTime(r.printedDate) },
    { field: 'templateName', headerName: t('history.template') },
    { field: 'labelCount', headerName: t('reprint.labelCount'), render: (r) => r.labels.length },
    { field: 'totalQty', headerName: t('history.quantity') },
    { field: 'printer', headerName: t('history.printer') },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) => (
        <Button size="small" variant="outlined" startIcon={<PrintIcon fontSize="small" />} onClick={() => reprintMutation.mutate(r)} disabled={reprintMutation.isPending}>
          {t('reprint.reprintButton')}
        </Button>
      ),
    },
  ];

  return (
    <>
      <GlobalStyles
        styles={{
          '@media print': {
            'body *': { visibility: 'hidden' },
            '#label-print-area, #label-print-area *': { visibility: 'visible' },
            '#label-print-area': { position: 'absolute', top: 0, left: 0, margin: 0 },
          },
          '@page': {
            size: printBatchTemplate ? `${printBatchTemplate.widthMm}mm ${printBatchTemplate.heightMm}mm` : 'auto',
            margin: 0,
          },
        }}
      />

      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      <Tabs value={pageTab} onChange={(_, v: 'print' | 'reprint') => setPageTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="print" label={t('tabs.print')} />
        <Tab value="reprint" label={t('tabs.reprint')} />
      </Tabs>

      {pageTab === 'print' && (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('approvedOnly')}
          </Alert>

          <GridContainer columns={12}>
            <GridItem xs={12} md={4}>
              <Stack spacing={3}>
                <Card>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <TuneIcon fontSize="small" color="primary" />
                      <Typography variant="h5">{t('selectionCriteria')}</Typography>
                    </Stack>
                    <Stack spacing={2}>
                      <SearchableSelect
                        label={t('selectReceipt')} fullWidth value={receiptId} onChange={setReceiptId}
                        options={selectableReceipts.map((r) => ({ value: r.id, label: `${r.receiptNumber} — ${r.productionOrderNumber ?? r.materialRequestNumber}` }))}
                      />
                      <SearchableSelect
                        label={t('selectItem')}
                        fullWidth
                        value={receiptLineId}
                        onChange={setReceiptLineId}
                        disabled={!receiptId}
                        helperText={receiptId && receiptLines.length === 0 ? t('noItemsOnReceipt') : undefined}
                        options={receiptLines.map((line) => ({ value: line.id, label: `${line.materialCode} — ${line.materialName}` }))}
                      />
                      <SearchableSelect
                        label={t('selectTemplate')}
                        fullWidth
                        value={templateId}
                        onChange={setTemplateId}
                        disabled={!receiptLineId}
                        helperText={receiptLineId && availableTemplates.length === 0 ? t('noTemplatesAvailable') : undefined}
                        options={availableTemplates.map((tpl) => ({ value: tpl.id, label: tpl.name }))}
                      />
                    </Stack>
                  </CardContent>
                </Card>

                {selectedLine && (
                  <Card>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <InfoOutlinedIcon fontSize="small" color="primary" />
                        <Typography variant="h5">{t('itemDetails')}</Typography>
                      </Stack>
                      <GridContainer columns={12} spacing={2}>
                        <GridItem xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                            {t('itemFields.batchNo')}
                          </Typography>
                          <Typography variant="body2">{selectedLine.batchNo ?? '—'}</Typography>
                        </GridItem>
                        <GridItem xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                            {t('itemFields.acceptedQty')}
                          </Typography>
                          <Typography variant="body2">{maxQty}</Typography>
                        </GridItem>
                        <GridItem xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                            {t('itemFields.labelPrintedQty')}
                          </Typography>
                          <Typography variant="body2">{selectedLine.labelPrintedQty}</Typography>
                        </GridItem>
                      </GridContainer>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <PrintIcon fontSize="small" color="primary" />
                      <Typography variant="h5">{t('configuration')}</Typography>
                    </Stack>

                    <ToggleButtonGroup
                      color="primary"
                      exclusive
                      fullWidth
                      size="small"
                      value={printMode}
                      onChange={(_, value: 'auto' | 'manual' | null) => value && setPrintMode(value)}
                      sx={{ mb: 2 }}
                    >
                      <ToggleButton value="auto">{t('mode.auto')}</ToggleButton>
                      <ToggleButton value="manual">{t('mode.manual')}</ToggleButton>
                    </ToggleButtonGroup>

                    {printMode === 'auto' ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {selectedMaterial?.stuffingQty && selectedMaterial.stuffingQty > 1
                            ? t('mode.autoHint', { total: remainingQty, stuffingQty: selectedMaterial.stuffingQty })
                            : t('mode.autoNoStuffingQty', { total: remainingQty })}
                        </Typography>
                        {autoBreakdown.length > 0 && (
                          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                            {autoBreakdown.map((q, i) => (
                              <Chip key={i} size="small" label={`${t('mode.labelN', { n: i + 1 })}: ${q}`} />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ mb: 2 }}>
                        <Stack spacing={1}>
                          {manualRows.map((qty, i) => (
                            <Stack key={i} direction="row" spacing={1} alignItems="center">
                              <TextField
                                size="small"
                                type="number"
                                label={t('mode.labelN', { n: i + 1 })}
                                fullWidth
                                value={qty}
                                onChange={(e) => {
                                  const value = Number(e.target.value);
                                  setManualRows((rows) => rows.map((r, idx) => (idx === i ? value : r)));
                                }}
                              />
                              <IconButton
                                size="small"
                                disabled={manualRows.length <= 1}
                                onClick={() => setManualRows((rows) => rows.filter((_, idx) => idx !== i))}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ))}
                        </Stack>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => setManualRows((rows) => [...rows, Math.max(0, remainingQty - rows.reduce((sum, q) => sum + q, 0))])}
                          sx={{ mt: 1 }}
                        >
                          {t('mode.addLabel')}
                        </Button>
                        <Typography variant="caption" display="block" color={manualValid ? 'text.secondary' : 'error'} sx={{ mt: 1 }}>
                          {t('mode.allocatedOf', { allocated: manualTotal, total: remainingQty })}
                          {!manualValid && ` — ${t('mode.mismatchHint', { total: remainingQty })}`}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 2.5 }} />
                    <Button fullWidth size="large" variant="contained" startIcon={<PrintIcon />} onClick={() => printMutation.mutate()} disabled={printMutation.isPending || !readyToPrint}>
                      {printMutation.isPending ? t('sendingToPrinter') : t('printLabels', { count: labelQuantities.length || 0 })}
                    </Button>
                  </CardContent>
                </Card>
              </Stack>
            </GridItem>

            <GridItem xs={12} md={8}>
              <Card sx={{ height: '100%' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ textTransform: 'uppercase' }}>
                    {t('canvasPreviewTitle')}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {selectedTemplate && <Chip size="small" label={`${selectedTemplate.widthMm}×${selectedTemplate.heightMm} mm`} />}
                    {selectedTemplate && labelQuantities.length > 0 && (
                      <Chip size="small" color="primary" variant="filled" label={t('printLabels', { count: labelQuantities.length })} />
                    )}
                  </Stack>
                </Stack>
                <Box sx={{ minHeight: 480, maxHeight: 640, overflow: 'auto', display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', gap: 2, bgcolor: 'action.hover', p: 3 }}>
                  {!selectedTemplate ? (
                    <Box sx={{ width: '100%', minHeight: 480, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('selectTemplatePrompt')}
                      </Typography>
                    </Box>
                  ) : labelQuantities.length === 0 ? (
                    <Box sx={{ width: '100%', minHeight: 480, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('noLabelsToPreview')}
                      </Typography>
                    </Box>
                  ) : (
                    labelQuantities.map((qty, i) => (
                      <Stack key={i} spacing={0.5} alignItems="center">
                        <Box
                          sx={{
                            position: 'relative',
                            width: selectedTemplate.widthMm * PREVIEW_SCALE,
                            height: selectedTemplate.heightMm * PREVIEW_SCALE,
                            bgcolor: '#fff',
                            boxShadow: '0px 4px 14px rgba(0,0,0,0.15)',
                          }}
                        >
                          {selectedTemplate.objects.map((obj) => {
                            // Same substitution the print path performs, so what is previewed is
                            // what prints.
                            const values = previewValues?.get(obj.id);
                            const instance = values?.[i] != null ? applySequenceValue(obj, values[i]) : obj;
                            return (
                              <LabelObjectRenderer key={obj.id} object={instance} sampleData={{ ...sampleData, Qty: String(qty) }} scale={PREVIEW_SCALE} />
                            );
                          })}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('mode.labelN', { n: i + 1 })}
                        </Typography>
                      </Stack>
                    ))
                  )}
                </Box>
              </Card>
            </GridItem>
          </GridContainer>

          <Box sx={{ mt: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <HistoryIcon fontSize="small" color="primary" />
              <Typography variant="h5">{t('history.title')}</Typography>
            </Stack>
            <DataTable columns={historyColumns} rows={history} searchable exportFileName="production-print-history" />
          </Box>
        </>
      )}

      {pageTab === 'reprint' && (
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <TuneIcon fontSize="small" color="primary" />
                <Typography variant="h5">{t('reprint.selectionCriteria')}</Typography>
              </Stack>
              <GridContainer columns={12} spacing={2}>
                <GridItem xs={12} md={6}>
                  <SearchableSelect
                    label={t('selectReceipt')}
                    fullWidth
                    value={reprintReceiptId}
                    onChange={setReprintReceiptId}
                    helperText={reprintableReceipts.length === 0 ? t('reprint.noPrintedReceipts') : undefined}
                    options={reprintableReceipts.map((r) => ({ value: r.id, label: `${r.receiptNumber} — ${r.productionOrderNumber ?? r.materialRequestNumber}` }))}
                  />
                </GridItem>
                <GridItem xs={12} md={6}>
                  <SearchableSelect
                    label={t('selectItem')}
                    fullWidth
                    value={reprintReceiptLineId}
                    onChange={setReprintReceiptLineId}
                    disabled={!reprintReceiptId}
                    helperText={reprintReceiptId && reprintReceiptLines.length === 0 ? t('reprint.noPrintedItems') : undefined}
                    options={reprintReceiptLines.map((line) => ({ value: line.id, label: `${line.materialCode} — ${line.materialName}` }))}
                  />
                </GridItem>
              </GridContainer>
            </CardContent>
          </Card>

          {reprintReceiptLineId && (
            <DataTable columns={reprintColumns} rows={reprintJobGroups} searchable={false} exportable={false} emptyMessage={t('reprint.noHistory')} />
          )}
        </Stack>
      )}

      <Box id="label-print-area" sx={{ display: 'none', '@media print': { display: 'block' } }}>
        {printBatch?.map((objects, i) => {
          const labelSampleData = { ...(printBatchSampleData ?? {}), Qty: String(printBatchQuantities?.[i] ?? '') };
          return (
            <Box
              key={i}
              sx={{
                position: 'relative',
                width: `${printBatchTemplate?.widthMm ?? 100}mm`,
                height: `${printBatchTemplate?.heightMm ?? 60}mm`,
                breakAfter: i === printBatch.length - 1 ? 'auto' : 'page',
                breakInside: 'avoid',
                pageBreakAfter: i === printBatch.length - 1 ? 'auto' : 'always',
                pageBreakInside: 'avoid',
              }}
            >
              {objects.map((obj) => (
                <LabelObjectRenderer key={obj.id} object={obj} sampleData={labelSampleData} scale={3.7795} />
              ))}
            </Box>
          );
        })}
      </Box>
    </>
  );
}
